import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Sparkles, X, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30000;
const ROTATE_MS = 9000;

/**
 * SocialProofWidgets — uses ONLY real `payment_transactions` data.
 *
 * 1. Launch progress banner — "X / 100 sold at launch price" (always shows)
 * 2. Rotating recent-activity toast — only renders when there are real
 *    paid purchases with derivable first names (no synthetic fill).
 */
export const SocialProofWidgets = () => {
  const [stats, setStats] = useState(null);
  const [activityIdx, setActivityIdx] = useState(0);
  const [activityVisible, setActivityVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const rotateRef = useRef(null);

  useEffect(() => {
    const fetchStats = () => {
      axios.get(`${API}/ebook/stats`).then((res) => setStats(res.data)).catch(() => {});
    };
    fetchStats();
    const id = setInterval(fetchStats, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!stats?.recent_purchases?.length || dismissed) return;
    rotateRef.current = setInterval(() => {
      setActivityVisible(false);
      setTimeout(() => {
        setActivityIdx((i) => (i + 1) % stats.recent_purchases.length);
        setActivityVisible(true);
      }, 350);
    }, ROTATE_MS);
    return () => clearInterval(rotateRef.current);
  }, [stats?.recent_purchases?.length, dismissed]);

  if (!stats) return null;

  const current = stats.recent_purchases?.[activityIdx];
  const showActivity = !dismissed && Boolean(current);

  return (
    <>
      <LaunchProgressBanner stats={stats} />

      {showActivity && (
        <div className="fixed bottom-5 left-5 z-40 max-w-sm" data-testid="social-proof-toast">
          <AnimatePresence mode="wait">
            {activityVisible && (
              <motion.div
                key={`${current.name}-${activityIdx}`}
                initial={{ opacity: 0, y: 16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.95 }}
                transition={{ duration: 0.35 }}
                className="glass-silver rounded-2xl px-4 py-3 pr-9 shadow-xl flex items-center gap-3 border border-silver/40"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-maroon flex items-center justify-center text-primary-foreground shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate" data-testid="social-proof-name">
                    {current.name} just bought the E-Book
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {current.time_ago} · verified purchase
                  </p>
                </div>
                <button
                  className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setDismissed(true)}
                  aria-label="Dismiss"
                  data-testid="social-proof-dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};

const LaunchProgressBanner = ({ stats }) => {
  const { total_sold, launch_goal, launch_remaining, launch_progress_pct } = stats;
  if (launch_remaining === 0) return null; // Launch closed — hide

  return (
    <div className="border-b border-secondary/30 bg-gradient-to-r from-primary-dark via-primary to-maroon text-primary-foreground" data-testid="launch-banner">
      <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-sm">
        <div className="flex items-center gap-2 font-semibold">
          <Flame className="w-4 h-4 text-secondary" />
          <span data-testid="launch-banner-text">
            Launch pricing — first <span className="text-secondary">{launch_goal}</span> readers only
          </span>
        </div>
        <div className="flex items-center gap-3 min-w-[220px] flex-1 max-w-md">
          <div className="flex-1 h-2 rounded-full bg-white/15 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(launch_progress_pct, 3)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-secondary to-secondary-light shadow-gold"
              data-testid="launch-progress-bar"
            />
          </div>
          <span className="text-xs whitespace-nowrap text-primary-foreground/90">
            <span className="text-secondary font-bold" data-testid="launch-sold">{total_sold}</span>
            <span className="opacity-60"> / {launch_goal} sold</span>
          </span>
        </div>
        {launch_remaining <= 20 && (
          <span className="text-xs px-2 py-1 rounded-full bg-secondary/25 text-secondary font-semibold">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Only {launch_remaining} spots left
          </span>
        )}
      </div>
    </div>
  );
};

export default SocialProofWidgets;
